import * as React from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  InputGroup,
  InputGroupItem,
  MenuToggle,
  MenuToggleElement,
  SearchInput,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
} from '@patternfly/react-core';
import { IfFeature } from '~/feature-flags/hooks';
import { SEARCH_SHORTCUT_KEY, useSearchShortcut } from '~/shared/hooks/useSearchShortcut';
import { useDebounceCallback } from '../../../shared/hooks/useDebounceCallback';
import ColumnManagementButton from '../components/ColumnManagementButton';

type BaseTextFilterToolbarProps = {
  text: string;
  label: string;
  setText: (value: string, searchType?: string) => void;
  onClearFilters: () => void;
  children?: React.ReactNode;
  dataTest?: string;
  openColumnManagement?: () => void;
  totalColumns?: number;
  showSearchInput?: boolean;
  noLeftPadding?: boolean;
  searchOptions?: string[];
  className?: string;
};

export const BaseTextFilterToolbar: React.FC<BaseTextFilterToolbarProps> = ({
  text,
  label,
  setText,
  onClearFilters,
  children,
  dataTest,
  openColumnManagement,
  totalColumns = 0,
  showSearchInput = true,
  noLeftPadding = false,
  searchOptions = [],
}) => {
  const [isOpen, setIsOpen] = React.useState<boolean>(false);
  const [searchOption, setSearchOption] = React.useState<string>(searchOptions?.[0] ?? '');
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const onTextInput = useDebounceCallback((value: string) => {
    setText(value, searchOption);
  }, 600);

  useSearchShortcut({ inputRef: searchInputRef, enabled: showSearchInput });

  const searchInput = React.useMemo(
    () => (
      <SearchInput
        ref={searchInputRef}
        name={`${label}Input`}
        data-test={`${label}-input-filter`}
        type="search"
        aria-label={`${label} filter`}
        inputProps={{ 'aria-keyshortcuts': SEARCH_SHORTCUT_KEY }}
        placeholder={`Filter by ${searchOptions.length > 0 ? searchOption.toLocaleLowerCase() : label}...`}
        onChange={(_, value) => onTextInput(value)}
        value={text}
      />
    ),
    [text, label, onTextInput, searchOption, searchOptions],
  );
  const searchGroup = React.useMemo(
    () => (
      <InputGroup>
        <InputGroupItem>
          <Dropdown
            isOpen={isOpen}
            onSelect={(_, value) => {
              const selected = String(value ?? '');
              setSearchOption(selected);
              setText('', selected);
              setIsOpen(false);
            }}
            toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
              <MenuToggle ref={toggleRef} onClick={() => setIsOpen(!isOpen)} isExpanded={isOpen}>
                {searchOption}
              </MenuToggle>
            )}
          >
            <DropdownList>
              {searchOptions.map((option) => (
                <DropdownItem key={option} value={option}>
                  {option}
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
        </InputGroupItem>
        <InputGroupItem isFill>{searchInput}</InputGroupItem>
      </InputGroup>
    ),
    [searchOptions, isOpen, searchInput, searchOption, setText],
  );

  return (
    <Toolbar data-test={dataTest} clearAllFilters={onClearFilters} className="pf-v6-u-py-md">
      <ToolbarContent style={{ paddingLeft: noLeftPadding ? '0' : undefined }}>
        {showSearchInput && (
          <ToolbarItem className="pf-v6-u-ml-0">
            {searchOptions.length > 0 ? searchGroup : searchInput}
          </ToolbarItem>
        )}
        {React.Children.map(children, (child, index) => (
          <ToolbarItem alignSelf="center" key={index}>
            {child}
          </ToolbarItem>
        ))}
        <IfFeature flag="column-management">
          <ToolbarItem>
            <ColumnManagementButton onClick={openColumnManagement} totalColumns={totalColumns} />
          </ToolbarItem>
        </IfFeature>
      </ToolbarContent>
    </Toolbar>
  );
};
